import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useQuery, UseQueryResult } from 'react-query';
import { GoogleCalendarAutoComplete, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams } from 'utils/functions';

interface IUseAutoCompleteGoogleCalendars {
  search?: string;
  ordering?: string;
  googleCalendarIntegrationId: string;
  isWriteable?: boolean;
}

function useAutoCompleteGoogleCalendars({
  search,
  ordering,
  googleCalendarIntegrationId,
  isWriteable,
}: IUseAutoCompleteGoogleCalendars): UseQueryResult<AxiosResponse<GoogleCalendarAutoComplete[]>> {
  const fetchAutoCompleteLocations = useCallback((): Promise<
    AxiosResponse<GoogleCalendarAutoComplete[]>
  > => {
    const params: UrlParams = { limit: '100', offset: '0' };

    if (ordering) {
      params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    }
    if (search) {
      params.search = search;
    }
    if (googleCalendarIntegrationId) {
      params.google_calendar_integration_id = googleCalendarIntegrationId;
    }
    if (isWriteable !== undefined) {
      params.is_writeable = isWriteable ? 'True' : 'False';
    }

    return client.get(`google-calendars/auto_complete/?${encodeUrlParams(params)}`);
  }, [ordering, search, googleCalendarIntegrationId, isWriteable]);

  return useQuery(
    ['google-calendars', 'autocomplete', ordering, search, isWriteable, googleCalendarIntegrationId],
    fetchAutoCompleteLocations,
    {
      enabled: !!googleCalendarIntegrationId,
    },
  );
}

export default useAutoCompleteGoogleCalendars;
